import type {
  Rule,
  RuleContext,
  RuleResult,
  GlobalVars,
  StyleTypes,
  SimplifiedLayout,
  SimplifiedTextStyle,
  SimplifiedFill,
  SimplifiedStroke,
  SimplifiedEffect
} from "../types/index.js";
import { logger } from "../utils/logger.js";

/**
 * 提取器函数类型
 */
export type ExtractorFn = (
  node: any,
  result: Record<string, any>,
  context: { globalVars: GlobalVars; parent?: any; depth: number; path: string[] }
) => void;

/**
 * 辅助函数：生成变量ID
 */
function generateVarId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * 辅助函数：查找或创建全局变量
 */
function findOrCreateVar(globalVars: GlobalVars, value: StyleTypes, prefix: string): string {
  // 检查是否已存在相同的值
  const [existingVarId] = Object.entries(globalVars.styles).find(
    ([_, existingValue]) => JSON.stringify(existingValue) === JSON.stringify(value)
  ) ?? [];

  if (existingVarId) {
    return existingVarId;
  }

  // 创建新变量
  const varId = generateVarId(prefix);
  globalVars.styles[varId] = value;
  return varId;
}

/**
 * 布局提取器
 */
export const layoutExtractor: ExtractorFn = (node, result, context) => {
  const layout = buildSimplifiedLayout(node, context.parent);
  if (Object.keys(layout).length > 1) {
    result.layout = findOrCreateVar(context.globalVars, layout, "layout");
  }
};

/**
 * 文本提取器
 */
export const textExtractor: ExtractorFn = (node, result, context) => {
  // 提取文本内容
  if (isTextNode(node)) {
    result.text = extractNodeText(node);
  }

  // 提取文本样式
  if (hasTextStyle(node)) {
    const textStyle = extractTextStyle(node);
    if (textStyle) {
      result.textStyle = findOrCreateVar(context.globalVars, textStyle, "textStyle");
    }
  }
};

/**
 * 视觉样式提取器
 */
export const visualsExtractor: ExtractorFn = (node, result, context) => {
  const hasChildren = hasValue("children", node) && Array.isArray(node.children) && node.children.length > 0;

  // 填充样式
  if (hasValue("fills", node) && Array.isArray(node.fills) && node.fills.length) {
    const fills = node.fills.map((fill: any) => parseFill(fill, hasChildren)).reverse();
    result.fills = findOrCreateVar(context.globalVars, fills, "fill");
  }

  // 描边样式
  const strokes = buildSimplifiedStrokes(node, hasChildren);
  if (strokes.colors.length) {
    result.strokes = findOrCreateVar(context.globalVars, strokes, "stroke");
  }

  // 效果样式
  if (hasValue("effects", node) && Array.isArray(node.effects) && node.effects.length) {
    const effects = node.effects.map((effect: any) => parseEffect(effect));
    result.effects = findOrCreateVar(context.globalVars, effects, "effect");
  }
};

/**
 * 组件提取器
 */
export const componentExtractor: ExtractorFn = (node, result, _context) => {
  if (hasValue("componentId", node) || hasValue("componentPropertyReferences", node)) {
    // 处理组件实例
    if (hasValue("componentId", node)) {
      result.componentId = node.componentId;
    }
  }
};

/**
 * 构建简化的布局
 */
function buildSimplifiedLayout(node: any, parent?: any): SimplifiedLayout {
  const layout: SimplifiedLayout = { mode: "none" };

  // 检查是否为自动布局
  if (hasValue("layoutMode", node)) {
    switch (node.layoutMode) {
      case "HORIZONTAL":
        layout.mode = "row";
        break;
      case "VERTICAL":
        layout.mode = "column";
        break;
      default:
        layout.mode = "none";
    }
  }

  // 添加其他布局属性
  if (hasValue("primaryAxisAlignItems", node)) {
    layout.justifyContent = convertAlignment(node.primaryAxisAlignItems);
  }

  if (hasValue("counterAxisAlignItems", node)) {
    layout.alignItems = convertAlignment(node.counterAxisAlignItems);
  }

  if (hasValue("itemSpacing", node)) {
    layout.gap = `${node.itemSpacing}px`;
  }

  // 尺寸信息
  if (hasValue("absoluteBoundingBox", node)) {
    layout.dimensions = {
      width: node.absoluteBoundingBox.width,
      height: node.absoluteBoundingBox.height
    };
  }

  // 位置信息
  if (hasValue("relativeTransform", node) && parent) {
    layout.locationRelativeToParent = {
      x: node.relativeTransform[0][2] || 0,
      y: node.relativeTransform[1][2] || 0
    };
  }

  return layout;
}

/**
 * 提取文本内容
 */
function extractNodeText(node: any): string {
  if (hasValue("characters", node)) {
    return node.characters;
  }
  return "";
}

/**
 * 提取文本样式
 */
function extractTextStyle(node: any): SimplifiedTextStyle | null {
  const style: SimplifiedTextStyle = {};

  if (hasValue("style", node)) {
    const textStyle = node.style;
    
    if (textStyle.fontFamily) style.fontFamily = textStyle.fontFamily;
    if (textStyle.fontWeight) style.fontWeight = textStyle.fontWeight;
    if (textStyle.fontSize) style.fontSize = textStyle.fontSize;
    if (textStyle.lineHeightPx) style.lineHeight = textStyle.lineHeightPx;
    if (textStyle.letterSpacing) style.letterSpacing = textStyle.letterSpacing;
    if (textStyle.textAlignHorizontal) {
      style.textAlign = convertTextAlign(textStyle.textAlignHorizontal);
    }
  }

  return Object.keys(style).length > 0 ? style : null;
}

/**
 * 解析填充样式
 */
function parseFill(fill: any, _hasChildren: boolean): SimplifiedFill {
  const result: SimplifiedFill = {
    type: "solid"
  };

  switch (fill.type) {
    case "SOLID":
      result.type = "solid";
      result.color = rgbToHex(fill.color);
      result.opacity = fill.opacity ?? 1;
      break;
    
    case "GRADIENT_LINEAR":
    case "GRADIENT_RADIAL":
    case "GRADIENT_ANGULAR":
      result.type = "gradient";
      result.gradient = {
        type: fill.type.toLowerCase().replace("gradient_", ""),
        stops: fill.gradientStops?.map((stop: any) => ({
          color: rgbToHex(stop.color),
          position: stop.position
        })) || []
      };
      break;
    
    case "IMAGE":
      result.type = "image";
      result.imageRef = fill.imageRef;
      break;
  }

  return result;
}

/**
 * 构建简化的描边样式
 */
function buildSimplifiedStrokes(node: any, _hasChildren: boolean): SimplifiedStroke {
  const result: SimplifiedStroke = {
    colors: [],
    width: 0
  };

  if (hasValue("strokes", node) && Array.isArray(node.strokes)) {
    result.colors = node.strokes.map((stroke: any) => rgbToHex(stroke.color));
  }

  if (hasValue("strokeWeight", node)) {
    result.width = node.strokeWeight;
  }

  if (hasValue("strokeAlign", node)) {
    result.position = node.strokeAlign.toLowerCase();
  }

  return result;
}

/**
 * 解析效果样式
 */
function parseEffect(effect: any): SimplifiedEffect {
  const result: SimplifiedEffect = {
    type: "drop-shadow"
  };

  switch (effect.type) {
    case "DROP_SHADOW":
      result.type = "drop-shadow";
      result.color = rgbToHex(effect.color);
      result.offset = { x: effect.offset?.x || 0, y: effect.offset?.y || 0 };
      result.radius = effect.radius || 0;
      result.spread = effect.spread || 0;
      break;
    
    case "INNER_SHADOW":
      result.type = "inner-shadow";
      result.color = rgbToHex(effect.color);
      result.offset = { x: effect.offset?.x || 0, y: effect.offset?.y || 0 };
      result.radius = effect.radius || 0;
      break;
    
    case "LAYER_BLUR":
      result.type = "blur";
      result.radius = effect.radius || 0;
      break;
    
    case "BACKGROUND_BLUR":
      result.type = "background-blur";
      result.radius = effect.radius || 0;
      break;
  }

  return result;
}

/**
 * 工具函数
 */
function hasValue(key: string, obj: any): boolean {
  return obj && obj[key] !== undefined && obj[key] !== null;
}

function isTextNode(node: any): boolean {
  return node.type === "TEXT";
}

function hasTextStyle(node: any): boolean {
  return hasValue("style", node);
}

function convertAlignment(align: string): "flex-start" | "flex-end" | "center" | "space-between" {
  switch (align) {
    case "MIN": return "flex-start";
    case "MAX": return "flex-end";
    case "CENTER": return "center";
    case "SPACE_BETWEEN": return "space-between";
    default: return "flex-start";
  }
}

function convertTextAlign(align: string): "left" | "center" | "right" | "justify" {
  switch (align) {
    case "LEFT": return "left";
    case "CENTER": return "center";
    case "RIGHT": return "right";
    case "JUSTIFIED": return "justify";
    default: return "left";
  }
}

function rgbToHex(rgb: any): string {
  if (!rgb) return "#000000";
  
  const r = Math.round((rgb.r || 0) * 255);
  const g = Math.round((rgb.g || 0) * 255);
  const b = Math.round((rgb.b || 0) * 255);
  
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * 数据提取验证规则
 */
export const dataExtractionValidationRule: Rule = {
  name: "data-extraction-validation",
  description: "验证 Figma 数据是否能够正确提取和转换",
  category: "structure",
  severity: "error",
  enabled: true,
  validate: async (context: RuleContext): Promise<RuleResult> => {
    const traceId = context.traceId;
    logger.info(`[${traceId}] 开始验证数据提取`);

    try {
      const issues: string[] = [];
      const suggestions: string[] = [];

      // 检查节点是否有基本属性
      if (!context.figmaFile) {
        issues.push("缺少 Figma 文件数据");
      }

      // 检查是否有可提取的内容
      const extractableNodes = countExtractableNodes(context.node);
      if (extractableNodes === 0) {
        issues.push("没有找到可提取的节点");
        suggestions.push("确保 Figma 文件包含可见的设计元素");
      }

      const valid = issues.length === 0;
      
      logger.info(`[${traceId}] 验证完成: ${valid ? "通过" : "失败"}, 问题数: ${issues.length}`);

      return {
        valid,
        message: valid ? "数据提取验证通过" : `发现 ${issues.length} 个问题: ${issues.join(", ")}`,
        severity: "error",
        suggestions
      };
    } catch (error) {
      logger.error(`[${traceId}] 验证失败: ${error}`);
      return {
        valid: false,
        message: `数据提取验证失败: ${error}`,
        severity: "error",
        suggestions: ["检查 Figma 数据格式是否正确"]
      };
    }
  }
};

/**
 * 计算可提取的节点数量
 */
function countExtractableNodes(node: any): number {
  if (!node) return 0;
  
  let count = 0;
  
  // 检查当前节点是否可提取
  if (node.visible !== false && node.type && node.type !== "DOCUMENT") {
    count++;
  }
  
  // 递归检查子节点
  if (node.children && Array.isArray(node.children)) {
    for (const child of node.children) {
      count += countExtractableNodes(child);
    }
  }
  
  return count;
}

/**
 * 所有提取器的组合
 */
export const allExtractors: ExtractorFn[] = [
  layoutExtractor,
  textExtractor,
  visualsExtractor,
  componentExtractor
];

/**
 * 仅布局和文本提取器
 */
export const layoutAndText: ExtractorFn[] = [
  layoutExtractor,
  textExtractor
];

/**
 * 仅内容提取器
 */
export const contentOnly: ExtractorFn[] = [
  textExtractor
];

/**
 * 仅视觉样式提取器
 */
export const visualsOnly: ExtractorFn[] = [
  visualsExtractor
];

/**
 * 仅布局提取器
 */
export const layoutOnly: ExtractorFn[] = [
  layoutExtractor
];